/**
 * Scheduling for speculative fetches — work the user has not asked for yet.
 * Anything that must happen for the page to be correct runs directly instead.
 */

/** Connection types too slow to spend on data the user may never reach. */
const BLOCKED_EFFECTIVE_TYPES = ["slow-2g", "2g"];

/**
 * Whether speculative work is worth its bandwidth on this connection.
 *
 * @returns {boolean}
 */
export const prefetchAllowed = () => {
  const connection = navigator?.connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  return !BLOCKED_EFFECTIVE_TYPES.includes(connection.effectiveType);
};

/**
 * Runs `callback` once the browser is idle, so speculative work never competes
 * with the frames that paint what the user is looking at.
 *
 * @param {() => void} callback
 * @param {{ timeout?: number }} options - Idle deadline before running anyway.
 * @returns {() => void} Cancels the pending callback.
 */
export const whenIdle = (callback, { timeout = 2000 } = {}) => {
  if (typeof requestIdleCallback === "undefined") {
    const timer = setTimeout(callback, 0);
    return () => clearTimeout(timer);
  }
  const handle = requestIdleCallback(callback, { timeout });
  return () => cancelIdleCallback(handle);
};
